<template>
	<div class="back-to-top">
		<transition name="fade">
			<button
				@click="backToTop"
				v-show="buttonIsVisible"
				class="button top-button"
			>
				<div class="top-button__text">Back to top</div>
				<Icon type="arrow-up" />
			</button>
		</transition>
		<v-breakpoint v-model="model" />
	</div>
</template>

<script>
import { Icon } from "../icon";
import { EventBus } from "@vg-faset/core";
import { VBreakpoint, Model } from "vue-breakpoint-component";
import debounce from "lodash/debounce";

/**
 * The Back to Top button:
 *
 * - is a button that takes the user back to the top of the screen
 *
 * - is fixed to bottom right corner of screen
 *
 * - is only visible when the page’s total height is greater that 300% of the window height and the scroll amount is at least 1.5x the window height
 *
 * - emits "scrolled" when clicked (in order to be backward compatible with PDP's 'Back to top' button)
 *
 */
export default {
	name: "BackToTop",
	components: {
		Icon,
		VBreakpoint
	},
	data() {
		return {
			buttonIsVisible: false,
			model: new Model()
		};
	},
	mounted() {
		window.smoothScroll = () => {
			const scrollTop = document.scrollingElement
					? document.scrollingElement.scrollTop // modern browser support
					: document.documentElement.scrollTop; // IE11 support

			if (scrollTop > 0) {
				window.requestAnimationFrame(window.smoothScroll);
				window.scrollTo(0, Math.floor(scrollTop - scrollTop / 6));
			}
		};
		window.addEventListener("scroll", this.onScroll);
	},
	destroyed() {
		window.removeEventListener("scroll", this.onScroll);
	},
	methods: {
		onScroll: debounce(function () {
			if (this.model.isSmall) {
				// Display 'Back to Top' button if the page's total height is greater than the window height
				// and the user has scrolled down 133% of the window height.
				this.buttonIsVisible =
					window.innerHeight < document.body.scrollHeight &&
					window.pageYOffset > window.innerHeight / 3;
			} else {
				// Display 'Back to Top' button if the page’s total height is greater that 300% of the window height
				// and the user has scrolled down 150% or more of the window height
				this.buttonIsVisible =
					window.innerHeight * 3 < document.body.scrollHeight &&
					window.pageYOffset > window.innerHeight * 1.5;
			}
		}, 10),
		backToTop() {
			this.buttonIsVisible = false;
			window.smoothScroll();
			EventBus.$emit("scrolled");
		}
	}
};
</script>

<style lang="scss" scoped>
.top-button {
	position: fixed;
	right: 32px;
	bottom: 32px;
	padding: 24px;
	z-index: 199; //one layer below modal
	border: 2px solid $color--neutral-10;
	background-clip: padding-box;
	height: 48px;
	line-height: 16px;
	overflow: visible;
	transition: all 0.2s ease-out;
	color: $color--neutral-10;
	display: block;
	background-image: radial-gradient(
			circle farthest-corner at 24px,
			$color--teal-2 24px,
			transparent 24px
		),
		linear-gradient(to right, transparent 24px, $color--teal-2 24px);

	&.fade-enter-active,
	&.fade-leave-active {
		transition: opacity 0.3s;
	}

	&.fade-enter-to,
	&.fade-leave-to {
		opacity: 0;
	}

	.top-button__text {
		opacity: 0;
		height: 0;
		width: 0;
		overflow: hidden;
		transition: opacity 0.3s 0.2s ease-out;
		position: relative;
		top: -6px;
	}

	.icon {
		box-sizing: content-box;
		border-radius: 50%;
		height: 44px;
		line-height: 48px;
		right: 0;
		width: 44px;
		color: $color--neutral-10;
		font-size: 24px;
		&::before {
			position: relative;
			top: -2px;
			left: -2px;
		}
	}

	@include hover {
		@include from-md {
			padding-right: 60px;
			transition: all 0.2s ease-out;
		}

		.top-button__text {
			@include from-md {
				opacity: 1;
				height: auto;
				width: auto;
			}
		}
	}
}
</style>
